import { describeError, unwrap } from '../../lib/api/client'
import type {
  BulkIn,
  CardOut,
  CaseListApiV1QueueCasesGetData,
  CaseRow,
  LanesOut,
  NextMoveIn,
  NextMoveOut,
  OwnerKind,
  PageCaseRow,
  ParkedActionIn,
  ParkedActionOut,
  ParkedTicketOut,
  SnoozeDefaultsOut,
  ThreadOut,
  TurnOut,
} from '../../lib/api/generated'
import {
  actOnParkedTicketApiV1QueueParkedDeskTicketIdActionPost,
  bulkApiV1QueueBulkPost,
  caseListApiV1QueueCasesGet,
  laneRowsApiV1QueueLanesLaneGet,
  lanesApiV1QueueLanesGet,
  listNextMovesApiV1CasesCaseIdNextMovesGet,
  listThreadsApiV1CasesCaseIdThreadsGet,
  markCaseReadApiV1CasesCaseIdReadPost,
  markCaseUnreadApiV1CasesCaseIdUnreadPost,
  meApiV1MeGet,
  needsUsNowApiV1QueueNeedsUsNowGet,
  noReplyNeededApiV1CasesCaseIdNoReplyNeededPost,
  nudgeApiV1CasesCaseIdNudgePost,
  parkedTicketsApiV1QueueParkedGet,
  readAllApiV1QueueReadAllPost,
  setNextMoveApiV1CasesCaseIdNextMovePut,
  setReplyClassificationApiV1CasesCaseIdReplyClassificationPost,
  snoozeDefaultsApiV1QueueSnoozeDefaultsGet,
  threadTurnsApiV1ThreadsThreadIdTurnsGet,
  undoNoReplyNeededApiV1CasesCaseIdNoReplyNeededDelete,
} from '../../lib/api/generated'
import type { ApiLane, NeedsUsFilter, QueueLaneSort } from './lanes'

/** Filters and sort for the All open / All cases / Closed views (`GET /queue/cases`). */
export interface CaseListParams {
  scope: 'open' | 'all' | 'closed'
  customer?: string
  status?: NonNullable<NonNullable<CaseListApiV1QueueCasesGetData['query']>['status']>
  lane?: ApiLane
  sort?: 'activity' | 'number' | 'customer' | 'lane' | 'due' | 'closed'
  order?: 'asc' | 'desc'
  /** Closed view (#271): `7` / `30` / `90` days back, `all`, or `custom` with `closedFrom`/`closedTo` (YYYY-MM-DD). */
  closed?: ClosedRange
  closedFrom?: string
  closedTo?: string
}

export type ClosedRange = '7' | '30' | '90' | 'all' | 'custom'
export const DEFAULT_CLOSED_RANGE: ClosedRange = '30'

function localDayStart(day: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(d.getTime()) ? null : d
}

/** The Closed view's `closed_from` / `closed_to` (ISO instants; `closed_to` is exclusive: the day after "To"). */
export function closedWindow(params: CaseListParams, now: Date = new Date()): { from?: string; to?: string } {
  if (params.scope !== 'closed') return {}
  const range = params.closed ?? DEFAULT_CLOSED_RANGE
  if (range === 'all') return {}
  if (range === 'custom') {
    const from = params.closedFrom ? localDayStart(params.closedFrom) : null
    const toDay = params.closedTo ? localDayStart(params.closedTo) : null
    const to = toDay ? new Date(toDay.getFullYear(), toDay.getMonth(), toDay.getDate() + 1) : null
    return { from: from?.toISOString(), to: to?.toISOString() }
  }
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - Number(range))
  return { from: start.toISOString() }
}

/** Fields added by api-queue-flags after the W0 OpenAPI snapshot. The coordinator regenerates the SDK at merge. */
export interface QueueBulkIn {
  case_ids: string[]
  action: 'assign' | 'owner' | 'next_move' | 'snooze' | 'close' | 'status' | 'priority' | 'nudge' | 'follow_up_owner' | 'merge' | 'link'
  plan_id: string
  initiated_by: 'human' | 'auto'
  owner_user_id?: string | null
  owner_desk_agent_id?: string | null
  owner_kind?: OwnerKind | null
  owner_contractor_id?: string | null
  next_move_text?: string | null
  due_at?: string | null
  status?: string | null
  resolution?: string | null
  priority?: string | null
  target_case_id?: string | null
  reason?: string | null
  snoozed_until?: string | null
  snooze_until_activity?: boolean | null
  snooze_reason?: string | null
}

export interface QueueExpansion {
  thread: ThreadOut | null
  lastTurn: TurnOut | null
  draft: TurnOut | null
  asOf: string | null
}

export interface QueueApi {
  lanes(signal?: AbortSignal, brand?: string): Promise<LanesOut>
  laneRows(lane: ApiLane, cursor: string | null, signal?: AbortSignal, sort?: QueueLaneSort, brand?: string): Promise<PageCaseRow>
  needsUsNow(filter: NeedsUsFilter, cursor: string | null, signal?: AbortSignal, brand?: string): Promise<PageCaseRow>
  caseList(params: CaseListParams, cursor: string | null, signal?: AbortSignal, brand?: string): Promise<PageCaseRow>
  expansion(caseId: string, signal?: AbortSignal): Promise<QueueExpansion>
  setNextMove(caseId: string, body: NextMoveIn): Promise<NextMoveOut>
  /** Riley's still-proposed next move for the case, if any (pre-fills the editor; the person still saves it). */
  proposal(caseId: string): Promise<NextMoveOut | null>
  /** Contractors a "waiting on contractor" owner can be (ranked for the case). Empty when none are known. */
  contractors(caseId: string): Promise<{ id: string; name: string }[]>
  take(caseIds: string[]): Promise<CardOut | CaseRow[]>
  bulk(body: QueueBulkIn): Promise<CardOut | CaseRow[]>
  /** Admin -> Settings snooze.defaults: the snooze popover's presets, default time and wake-on-activity. */
  snoozeDefaults(): Promise<SnoozeDefaultsOut>
  /** Overdue by technician `p`: proposes a post_note card; Riley posts the private note only once it is confirmed. */
  nudge(caseId: string): Promise<CardOut>
  /** #788: the newest received message needs no reply (clears Reply owed); `undo` owes it again. */
  noReplyNeeded(caseId: string): Promise<void>
  undoNoReplyNeeded(caseId: string): Promise<void>
  /** #928 exact newest customer turn; both values are append-only human training evidence. */
  setReplyClassification(caseId: string, classification: 'needs_reply' | 'no_reply_needed'): Promise<void>
  /** #876: this person opened the case / used Mark unread / Mark all read on the lane(s). Own state only. */
  markRead(caseId: string): Promise<void>
  markUnread(caseId: string): Promise<void>
  markAllRead(lanes: ApiLane[], brand?: string): Promise<number>
  /** Open Desk tickets that have not been formed into a case yet (#688). */
  parked(signal?: AbortSignal): Promise<ParkedTicketOut[]>
  /** A person's explicit local placement or disposition of a parked ticket. */
  actParked(deskTicketId: string, body: ParkedActionIn): Promise<ParkedActionOut>
}

function latest<T>(items: T[]): T | null {
  return items.length > 0 ? (items.at(-1) ?? null) : null
}

export const generatedQueueApi: QueueApi = {
  lanes: (signal, brand) => unwrap(lanesApiV1QueueLanesGet({ signal, query: { brand } })),

  laneRows: (lane, cursor, signal, sort, brand) =>
    unwrap(
      laneRowsApiV1QueueLanesLaneGet({
        path: { lane },
        query: { cursor, limit: 200, sort, brand },
        signal,
      }),
    ),

  needsUsNow: (filter, cursor, signal, brand) =>
    unwrap(
      needsUsNowApiV1QueueNeedsUsNowGet({
        query: { filter, cursor, limit: 200, brand },
        signal,
      }),
    ),

  caseList: (params, cursor, signal, brand) =>
    unwrap(
      caseListApiV1QueueCasesGet({
        query: {
          scope: params.scope,
          cursor,
          limit: 100,
          customer: params.customer?.trim() || undefined,
          status: params.status,
          lane: params.lane,
          sort: params.sort,
          order: params.order,
          closed_from: closedWindow(params).from,
          closed_to: closedWindow(params).to,
          brand,
        },
        signal,
      }),
    ),

  async expansion(caseId, signal) {
    const threads = await unwrap(listThreadsApiV1CasesCaseIdThreadsGet({ path: { case_id: caseId }, signal }))
    if (threads.length === 0) return { thread: null, lastTurn: null, draft: null, asOf: null }

    const results = await Promise.all(
      threads.map((thread) => unwrap(threadTurnsApiV1ThreadsThreadIdTurnsGet({ path: { thread_id: thread.id }, signal }))),
    )
    // Drafts are facts in the timeline but never answers. Keep them separate so the expanded row cannot imply otherwise.
    const turns = results.flatMap((result) => result.turns)
    const sentTurns = turns
      .filter((turn) => !turn.is_unsent_draft && turn.direction !== 'draft')
      .sort((left, right) => Date.parse(left.sent_at ?? '') - Date.parse(right.sent_at ?? ''))
    const drafts = turns.filter((turn) => turn.is_unsent_draft || turn.direction === 'draft')
    const lastTurn = latest(sentTurns)
    const draft = latest(drafts)
    const threadId = draft?.thread_id ?? lastTurn?.thread_id
    return {
      thread: threads.find((thread) => thread.id === threadId) ?? threads[0] ?? null,
      lastTurn,
      draft,
      asOf: results.find((result) => result.as_of)?.as_of ?? null,
    }
  },

  setNextMove: (caseId, body) => unwrap(setNextMoveApiV1CasesCaseIdNextMovePut({ path: { case_id: caseId }, body })),

  async proposal(caseId) {
    const moves = await unwrap(listNextMovesApiV1CasesCaseIdNextMovesGet({ path: { case_id: caseId } }))
    return moves.find((m) => m.state === 'proposed' && m.proposed_by === 'riley') ?? null
  },

  async contractors(caseId) {
    // #178: the whole directory (engaged firms first), not Conduit's top 10.
    const { fetchCandidates } = await import('../dispatch-picker/api')
    const { candidates } = await fetchCandidates(caseId, [], null)
    return candidates.filter((r) => r.selectable).map((r) => ({ id: r.contractor_id, name: r.name }))
  },

  async take(caseIds) {
    const me = await unwrap(meApiV1MeGet())
    return (await unwrap(
      bulkApiV1QueueBulkPost({
        body: {
          case_ids: caseIds,
          action: 'assign',
          owner_user_id: me.id,
          plan_id: globalThis.crypto.randomUUID(),
          initiated_by: 'human',
        } as BulkIn,
      }),
    )) as unknown as CardOut | CaseRow[]
  },

  bulk: async (body) => (await unwrap(bulkApiV1QueueBulkPost({ body: body as BulkIn }))) as unknown as CardOut | CaseRow[],

  snoozeDefaults: () => unwrap(snoozeDefaultsApiV1QueueSnoozeDefaultsGet()),

  nudge: (caseId) => unwrap(nudgeApiV1CasesCaseIdNudgePost({ path: { case_id: caseId }, body: {} })),
  noReplyNeeded: async (caseId) => {
    await unwrap(noReplyNeededApiV1CasesCaseIdNoReplyNeededPost({ path: { case_id: caseId }, body: {} }))
  },
  undoNoReplyNeeded: async (caseId) => {
    await unwrap(undoNoReplyNeededApiV1CasesCaseIdNoReplyNeededDelete({ path: { case_id: caseId } }))
  },
  setReplyClassification: async (caseId, classification) => {
    await unwrap(
      setReplyClassificationApiV1CasesCaseIdReplyClassificationPost({
        path: { case_id: caseId },
        body: { classification },
      }),
    )
  },

  markRead: async (caseId) => {
    await unwrap(markCaseReadApiV1CasesCaseIdReadPost({ path: { case_id: caseId } }))
  },
  markUnread: async (caseId) => {
    await unwrap(markCaseUnreadApiV1CasesCaseIdUnreadPost({ path: { case_id: caseId } }))
  },
  markAllRead: async (lanes, brand) => (await unwrap(readAllApiV1QueueReadAllPost({ query: { lane: lanes, brand } }))).marked,

  parked: (signal) => unwrap(parkedTicketsApiV1QueueParkedGet({ signal })),

  actParked: (deskTicketId, body) =>
    unwrap(actOnParkedTicketApiV1QueueParkedDeskTicketIdActionPost({ path: { desk_ticket_id: deskTicketId }, body })),
}

export function queueErrorText(error: unknown): string {
  return describeError(error)
}
